/*
 * Normalises POST /cm/outbox/conversations/<contactId> into a feed the panes can render.
 *
 * The response is `{ emails: { <threadId>: [Task] }, liMessages: [Task] }`, where either
 * key may be absent and the whole body may be null. `emails` is a server-side HashMap, so
 * neither thread order nor message order can be trusted -- everything is sorted here.
 *
 * One Task can carry TWO messages: the mail we sent (touchPoint.*) and, when replyCount is
 * set, the contact's reply (replyContent + touchPoint.replied*). `receivedEmail` tasks are
 * standalone messages that were never sent through Salesgear.
 */

const RECEIVED_EMAIL_TASK_TYPE = "receivedemail";
const LI_AUTO_TASK_TYPE = "linkedinauto";

const LI_STEP_LABELS = {
  LI_SEND_CONNECTION_REQUEST: "Connection Request",
  LI_SEND_MESSAGE: "LinkedIn Message",
  LI_SEND_INMAIL: "LinkedIn InMail",
  LI_VIEW_PROFILE: "View Profile",
  LI_INTERACT_WITH_POST: "Interact With a Post"
};

/*
 * "Step 2 — LinkedIn Message". `order` is 1-based (see EmailPreviewContentPopup:908).
 * Returns "" for anything that isn't a sequence step -- manual replies, received mail --
 * so the UI can simply skip the label.
 */
export const getStepLabel = (task = {}) => {
  const order = +task?.order || 0;
  if (!order) return "";
  const taskType = lower(task?.taskType);
  const liType = task?.linkedInTaskType || task?.touchPoint?.linkedInTaskType;
  if (liType) {
    return `Step ${order} — ${LI_STEP_LABELS[liType] || "LinkedIn"}`;
  }
  if (taskType === "manualemail") return `Step ${order} — Manual Email`;
  if (taskType === "email") return `Step ${order} — Auto Email`;
  return "";
};

// The step a thread represents = its furthest-along sequence step.
const getThreadStepLabel = (tasks = []) =>
  tasks
    .slice()
    .sort((a, b) => (+b?.order || 0) - (+a?.order || 0))
    .map(getStepLabel)
    .find(Boolean) || "";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

// "20 Jun '26" / "09:42 PM" -- the design splits date and time into separate slots.
export const formatDay = date =>
  date
    ? `${date.getDate()} ${MONTHS[date.getMonth()]} '${String(
        date.getFullYear()
      ).slice(-2)}`
    : "";

export const formatTime = date =>
  date
    ? date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      })
    : "";

const lower = (value = "") => (value || "").toString().trim().toLowerCase();

const toArray = value => {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).filter(Boolean);
};

const parseDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const time = date => (date ? date.getTime() : null);

// Nulls sort last so undated tasks never jump to the top of a thread.
const byDateAsc = (a, b) => {
  const [x, y] = [time(a?.date), time(b?.date)];
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return x - y;
};

const byLatestDesc = (a, b) => {
  const [x, y] = [time(a?.latestDate), time(b?.latestDate)];
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return y - x;
};

export const getInboxAddresses = (connectedInboxes = []) =>
  new Set(
    toArray(connectedInboxes)
      .map(item => lower(item?.email || item?.fromAddress || item))
      .filter(Boolean)
  );

const isOwnAddress = (address = "", inboxSet) =>
  inboxSet?.size > 0 && inboxSet.has(lower(address));

// Deliberately not Utils.convertDomToPlainText: Utils/utils runs side effects at import
// time (Driver.js), which would drag the whole app into this otherwise pure module.
export const toPreview = (body = "") => {
  if (!body) return "";
  const element = document.createElement("div");
  element.innerHTML = body.replace(/<!--[\s\S]*?-->/g, "");
  return (element.textContent || "").replace(/\s+/g, " ").trim();
};

const buildEmailMessages = (task = {}, inboxSet) => {
  const tp = task?.touchPoint || {};
  const taskType = lower(task?.taskType);

  if (taskType === RECEIVED_EMAIL_TASK_TYPE) {
    const from = tp?.fromAddress || "";
    // Only our own out-of-app send earns the "Sent via Gmail" tag; a contact mailing
    // into the thread is just an inbound message.
    const sentByUs = isOwnAddress(from, inboxSet);
    return [
      {
        id: task?.id,
        taskId: task?.id,
        dir: sentByUs ? "out" : "in",
        from,
        to: toArray(tp?.toAddress),
        cc: toArray(tp?.cc || tp?.ccAddress),
        subject: tp?.subject || task?.subject || "",
        body: tp?.content || tp?.shortContent || "",
        date: parseDate(task?.replyDate || task?.createTs || task?.sentDate),
        external: sentByUs
      }
    ];
  }

  const messages = [];
  const sentDate = parseDate(task?.sentDate || task?.createTs);
  const body = tp?.content || tp?.shortContent || "";

  if (sentDate || body) {
    messages.push({
      id: `${task?.id}-out`,
      taskId: task?.id,
      dir: "out",
      from: tp?.fromAddress || "",
      to: toArray(tp?.toAddress),
      cc: toArray(tp?.ccAddress),
      subject: tp?.subject || task?.subject || "",
      body,
      date: sentDate,
      external: false
    });
  }

  if (+task?.replyCount > 0 && (task?.replyContent || tp?.repliedFromAddress)) {
    messages.push({
      id: `${task?.id}-reply`,
      taskId: task?.id,
      dir: "in",
      from: tp?.repliedFromAddress || "",
      to: toArray(tp?.repliedToAddress),
      cc: toArray(tp?.repliedCcAddress),
      subject: tp?.repliedSubject || tp?.subject || "",
      body: task?.replyContent || "",
      date: parseDate(task?.replyDate) || sentDate,
      external: false
    });
  }

  return messages;
};

const buildLiMessages = (task = {}) => {
  const tp = task?.touchPoint || {};
  const sentDate = parseDate(
    task?.liMessageSentDate || task?.sentDate || task?.createTs
  );
  const messages = [];
  const body = tp?.text || task?.content || "";

  if (sentDate || body) {
    messages.push({
      id: `${task?.id}-out`,
      dir: "out",
      body,
      date: sentDate,
      // Only sequence steps carry a label; a manual reply (liReplyMessage) shows none.
      step:
        lower(task?.taskType) === LI_AUTO_TASK_TYPE ? getStepLabel(task) : ""
    });
  }

  if (+task?.replyCount > 0 && task?.replyContent) {
    messages.push({
      id: `${task?.id}-reply`,
      dir: "in",
      body: task?.replyContent,
      date: parseDate(task?.replyDate) || sentDate,
      step: ""
    });
  }

  return messages;
};

const aggregateStats = (tasks = []) =>
  tasks.reduce(
    (acc, task) => ({
      sent: acc.sent + (+task?.sentCount || 0),
      opens: acc.opens + (+task?.openCount || 0),
      clicks: acc.clicks + (+task?.clickCount || 0),
      replies: acc.replies + (+task?.replyCount || 0)
    }),
    { sent: 0, opens: 0, clicks: 0, replies: 0 }
  );

// A task counts toward unread only if it carries an INBOUND message. Our own sends -- an
// outbound sequence step, a reply we sent, or an out-of-app Gmail send (receivedEmail from
// one of our own inboxes) -- never make a conversation unread.
const hasInbound = (task = {}, inboxSet) => {
  if (lower(task?.taskType) === RECEIVED_EMAIL_TASK_TYPE) {
    return !isOwnAddress(task?.touchPoint?.fromAddress || "", inboxSet);
  }
  return +task?.replyCount > 0;
};

const collectUnread = (tasks = [], inboxSet) => {
  const taskIds = [];
  let unread = false;
  tasks.forEach(task => {
    if (task?.id) taskIds.push(task.id);
    if (lower(task?.readStatus) === "unread" && hasInbound(task, inboxSet)) {
      unread = true;
    }
  });
  return { unread, taskIds };
};

/*
 * sequenceId -> "Step N — ...", derived from the conversations, because ContactSequence
 * carries no step. Only sent tasks reach us, so this is the contact's furthest-executed
 * step in that sequence, not a scheduled upcoming one.
 */
export const getStepBySequence = (feed = []) =>
  feed.reduce((acc, item) => {
    if (item?.sequenceId && item?.step && !acc[item.sequenceId]) {
      acc[item.sequenceId] = item.step;
    }
    return acc;
  }, {});

export const normalizeConversations = (response, connectedInboxes = []) => {
  const { emails = {}, liMessages = [] } = response || {};
  const inboxSet = getInboxAddresses(connectedInboxes);
  const feed = [];

  Object.keys(emails || {}).forEach(threadId => {
    const tasks = toArray(emails[threadId]);
    const messages = tasks
      .reduce((acc, task) => acc.concat(buildEmailMessages(task, inboxSet)), [])
      .sort(byDateAsc);
    if (!messages.length) return;

    const last = messages[messages.length - 1];
    const { unread, taskIds } = collectUnread(tasks, inboxSet);
    feed.push({
      id: threadId,
      type: "email",
      subject: messages[0]?.subject || last?.subject || "",
      messages,
      stats: aggregateStats(tasks),
      sequenceId: tasks.map(task => task?.campaignSequenceId).find(Boolean) || "",
      step: getThreadStepLabel(tasks),
      from: last?.from || "",
      latestDate: last?.date || null,
      preview: toPreview(last?.body),
      externalGmail: messages.some(message => message?.external),
      unread,
      taskIds
    });
  });

  const liTasks = toArray(liMessages);
  if (liTasks.length) {
    // The design shows a single LinkedIn conversation per contact -- LinkedIn has no
    // thread id to split on, so every LI task collapses into one.
    const messages = liTasks
      .reduce((acc, task) => acc.concat(buildLiMessages(task)), [])
      .sort(byDateAsc);
    if (messages.length) {
      const last = messages[messages.length - 1];
      const { unread, taskIds } = collectUnread(liTasks);
      feed.push({
        id: "linkedin",
        type: "linkedin",
        messages,
        sequenceId:
          liTasks.map(task => task?.campaignSequenceId).find(Boolean) || "",
        step: getThreadStepLabel(liTasks),
        latestDate: last?.date || null,
        preview: toPreview(last?.body),
        externalGmail: false,
        unread,
        taskIds
      });
    }
  }

  return feed.sort(byLatestDesc);
};

export default normalizeConversations;
